import React from 'react';
import { ChevronDown, ChevronRight, LucideIcon } from 'lucide-react';
import { Badge } from './Badge';

export interface AccordionMenuItemProps {
  icon: LucideIcon;
  label: string;
  badge?: number;
  badgeVariant?: 'alert' | 'warning' | 'info' | 'success';
  isExpanded?: boolean;
  onToggle?: () => void;
  children?: React.ReactNode;
  isActive?: boolean;
  onClick?: () => void;
  hasChildren?: boolean;
  collapsed?: boolean;
  className?: string;
}

export const AccordionMenuItem: React.FC<AccordionMenuItemProps> = ({
  icon: Icon,
  label,
  badge,
  badgeVariant = 'alert',
  isExpanded = false,
  onToggle,
  children,
  isActive = false,
  onClick,
  hasChildren = false,
  collapsed = false,
  className = '',
}) => {
  const handleClick = () => {
    if (collapsed && onClick) {
      onClick();
    } else if (hasChildren && onToggle && !collapsed) {
      onToggle();
    } else if (onClick) {
      onClick();
    }
  };

  if (collapsed) {
    return (
      <div className={`relative group ${className}`}>
        <button
          onClick={handleClick}
          className={`w-full flex items-center justify-center p-3 rounded-2xl transition-all duration-300 ${
            isActive
              ? 'bg-white/50 backdrop-blur-sm text-teal-700 font-semibold shadow-lg border border-white/60'
              : 'text-gray-700 hover:bg-white/30 hover:backdrop-blur-sm border border-transparent'
          }`}
          title={label}
        >
          <div className="relative">
            <Icon className="w-5 h-5 flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:text-teal-600" />
            {badge !== undefined && badge > 0 && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full shadow-sm"></span>
            )}
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className={className}>
      <button
        onClick={handleClick}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl transition-all duration-300 ${
          isActive
            ? 'bg-white/50 backdrop-blur-sm text-teal-700 font-semibold shadow-lg border border-white/60'
            : 'text-gray-700 hover:bg-white/30 hover:backdrop-blur-sm border border-transparent'
        }`}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:text-teal-600" />
          <span className="font-medium">{label}</span>
          {badge !== undefined && badge > 0 && (
            <Badge variant={badgeVariant} size="sm">
              {badge}
            </Badge>
          )}
        </div>
        {hasChildren && (
          <div className="flex-shrink-0 ml-2">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </div>
        )}
      </button>

      {hasChildren && isExpanded && children && !collapsed && (
        <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
          {children}
        </div>
      )}
    </div>
  );
};

interface SubMenuItemProps {
  icon?: LucideIcon;
  label: string;
  badge?: number;
  badgeVariant?: 'alert' | 'warning' | 'info' | 'success';
  isActive?: boolean;
  onClick: () => void;
}

export const SubMenuItem: React.FC<SubMenuItemProps> = ({
  icon: Icon,
  label,
  badge,
  badgeVariant = 'alert',
  isActive = false,
  onClick,
}) => {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl transition-all duration-300 text-sm ${
        isActive
          ? 'bg-white/40 backdrop-blur-sm text-teal-700 font-semibold shadow-md border border-white/50'
          : 'text-gray-600 hover:bg-white/25 hover:backdrop-blur-sm hover:text-gray-900 border border-transparent'
      }`}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:text-teal-600" />}
        <span>{label}</span>
      </div>
      {badge !== undefined && badge > 0 && (
        <Badge variant={badgeVariant} size="sm">
          {badge}
        </Badge>
      )}
    </button>
  );
};

export default AccordionMenuItem;
